/** A suggested retail item is a shortcut to confirmation, never an inferred relationship. */
import { useState } from 'react'
import { knowledgeCandidateClues, type KnowledgeCatalogItem } from '@testfeed/integration-contracts'
import { storeKnowledgeCopy as copy } from '@testfeed/product-language'
import type { ShopifyTokenSource } from '../../shopifyApiFetch'
import { useStoreKnowledge } from '../../hooks/useStoreKnowledge'
import { KnowledgeItemPicker } from './KnowledgeItemPicker'
import { KnowledgeProduct } from './KnowledgeProduct'
import { trackShopifyAction } from '../../shopifyUiTracking'
function retailSearch(item: KnowledgeCatalogItem) {
  return (item.productTitle ?? item.title ?? '')
    .replace(/(^|[^a-z])(sample|trial|tester)(?=[^a-z]|$)/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}
export function KnowledgeTargetQuestion({
  shopify,
  item,
  suggestedTarget,
  busy,
  onChoose,
  onUnknown,
  onBack,
}: {
  shopify: ShopifyTokenSource
  item: KnowledgeCatalogItem
  suggestedTarget?: KnowledgeCatalogItem
  busy: boolean
  onChoose: (target: KnowledgeCatalogItem) => void
  onUnknown: () => void
  onBack: () => void
}) {
  const [choosing, setChoosing] = useState(false)
  const query = useStoreKnowledge(shopify, {
    search: retailSearch(item),
    suggestedOnly: false,
  })
  let suggested = suggestedTarget
  if (!suggested)
    suggested = query.data?.catalog.find(
      (target) =>
        (target.subject.productId !== item.subject.productId ||
          Boolean(
            item.subject.variantId && target.subject.variantId && target.subject.variantId !== item.subject.variantId,
          )) &&
        !knowledgeCandidateClues(target).length,
    )
  let selection = null
  let confirm = null
  let heading = copy.shortTargetQuestion as string
  if (suggested) {
    heading = copy.suggestedTargetQuestion
    selection = <KnowledgeProduct item={suggested} />
    confirm = (
      <s-button
        variant="primary"
        disabled={busy}
        loading={busy}
        onClick={() => trackShopifyAction(shopify, 'select-target', () => onChoose(suggested!))}
      >
        {copy.useProduct}
      </s-button>
    )
  }
  if (choosing) {
    selection = <KnowledgeItemPicker shopify={shopify} retail onSelect={(target) => onChoose(target)} />
    confirm = null
  }
  let chooseLabel = copy.chooseProduct as string
  if (suggested) chooseLabel = copy.chooseAnother
  return (
    <s-section heading={heading}>
      <s-stack gap="base">
        <KnowledgeProduct item={item}>
          <s-text color="subdued">{copy.targetReason}</s-text>
        </KnowledgeProduct>
        {selection}
        <s-stack direction="inline" gap="small">
          {confirm}
          <s-button
            disabled={busy}
            onClick={() => trackShopifyAction(shopify, 'start-answer', () => setChoosing(true))}
          >
            {chooseLabel}
          </s-button>
          <s-button disabled={busy} onClick={onUnknown}>
            {copy.shortNotSure}
          </s-button>
          <s-button disabled={busy} onClick={onBack}>
            {copy.back}
          </s-button>
        </s-stack>
      </s-stack>
    </s-section>
  )
}
